import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, TrendingDown, Package } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { dealsAPI } from '../lib/api';

export default function DealsShowcase() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await dealsAPI.showcase(3);
        setDeals(data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  if (loading || deals.length === 0) return null;

  return (
    <section className="py-16 md:py-20 bg-gradient-to-b from-white to-amber-50/30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3">
            <Sparkles className="w-3 h-3" /> Nouveau
          </span>
          <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl text-slate-900 mb-3">
            ISM Deals — Vous achetez, <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">nous regroupons</span>
          </h2>
          <p className="text-slate-600 text-base md:text-lg max-w-2xl mx-auto">
            Nous sélectionnons des produits en France, vous les achetez chez le marchand, nous les regroupons vers votre destination. Livraison mutualisée = coût optimisé.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {deals.map((d) => {
            const p = d.featured_product;
            return (
              <Link
                key={d.id}
                to={`/deals/${d.slug}`}
                className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-amber-400 hover:shadow-xl transition-all"
                data-testid={`landing-deal-${d.slug}`}
              >
                {/* Hero + product photo */}
                <div className="relative aspect-[4/3] bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 overflow-hidden">
                  {p?.product?.photos?.[0] ? (
                    <img src={p.product.photos[0]} alt={p.product.title} className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform" loading="lazy" />
                  ) : (
                    <div className="flex items-center justify-center h-full text-8xl">{d.destination?.flag}</div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
                  <div className="absolute top-3 left-3">
                    <span className="text-4xl drop-shadow">{d.destination?.flag}</span>
                  </div>
                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <div className="text-xs uppercase tracking-wider font-bold text-amber-300 mb-1">
                      {d.destination?.name}
                    </div>
                    <div className="text-lg font-black leading-tight" style={{fontFamily:'Chivo, sans-serif'}}>
                      {d.name.replace('ISM Deals — ', '')}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  {p?.product && (
                    <div className="text-xs text-slate-500 mb-3 uppercase tracking-wider">
                      Ex : <span className="font-semibold text-slate-700">{p.product.brand} {p.product.title.slice(0, 30)}…</span>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="bg-slate-50 rounded-lg px-2 py-1.5 text-center">
                      <div className="text-lg font-black text-slate-900">{d.products_count || 0}</div>
                      <div className="text-[10px] text-slate-500">produits</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg px-2 py-1.5 text-center">
                      <div className="text-lg font-black text-slate-900">{d.interests_count || 0}</div>
                      <div className="text-[10px] text-slate-500">intéressés</div>
                    </div>
                    <div className="bg-green-50 rounded-lg px-2 py-1.5 text-center">
                      <div className="text-lg font-black text-green-900">{d.received_orders_count || 0}</div>
                      <div className="text-[10px] text-green-700">reçues</div>
                    </div>
                  </div>
                  {p?.current_price && (
                    <div className="text-sm text-slate-600 mb-3">
                      Livraison groupée dès <strong className="text-amber-700 text-lg">{p.current_price.toFixed(2)} €</strong>
                    </div>
                  )}
                  <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white group-hover:bg-amber-500 group-hover:text-slate-900 transition-colors font-bold">
                    Découvrir →
                  </Button>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="text-center mt-8">
          <Link to="/deals">
            <Button variant="outline" className="border-2 border-amber-400 text-amber-700 hover:bg-amber-500 hover:text-slate-900 font-bold px-8" data-testid="landing-deals-all-btn">
              Voir toutes les opérations →
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
